// FORGED Ed Systems — DEPLOY003 — v0.3.0 — IntakeStep5CurrentState.tsx
// Last touched: 2026-05-19
//
// Step 5 of intake: Where the cohort is right now. Only shown when
// operating_mode is mid_year or cert_prep_window.

import type {
  CohortCurrentState,
  CohortSegment,
  WeldPositionCapability,
  WeldProcessType,
  NotableBlocker,
  BlockerType,
} from "./intakeTypes";

type Props = {
  rosterSize: number;
  value: CohortCurrentState;
  onChange: (block: CohortCurrentState) => void;
};

const POSITION_OPTIONS: { id: WeldPositionCapability; label: string }[] = [
  { id: "pre_1F", label: "Pre 1F" },
  { id: "1F_passing", label: "1F passing" },
  { id: "2F_passing", label: "2F passing" },
  { id: "3F_passing", label: "3F passing" },
  { id: "4F_passing", label: "4F passing" },
  { id: "1G_passing", label: "1G passing" },
  { id: "2G_passing", label: "2G passing" },
  { id: "3G_passing", label: "3G passing" },
  { id: "4G_passing", label: "4G passing" },
  { id: "5G_passing", label: "5G passing" },
  { id: "6G_passing", label: "6G passing" },
  { id: "mixed_advanced", label: "Mixed advanced" },
];

const PROCESS_OPTIONS: WeldProcessType[] = ["SMAW", "GMAW", "GTAW", "FCAW"];

const BLOCKER_OPTIONS: { id: BlockerType; label: string }[] = [
  { id: "equipment_downtime", label: "Equipment downtime" },
  { id: "consumables_shortage", label: "Consumables shortage" },
  { id: "schedule_disruption", label: "Schedule disruption" },
  { id: "instructor_absence", label: "Instructor absence" },
  { id: "facility_issue", label: "Facility issue" },
  { id: "other", label: "Other" },
];

function emptySegment(): CohortSegment {
  return {
    segment_label: "",
    student_count: 1,
    current_position_capability: "2F_passing",
    current_process_capability: ["SMAW"],
  };
}

export default function IntakeStep5CurrentState({ rosterSize, value, onChange }: Props) {
  function updateSegment(idx: number, patch: Partial<CohortSegment>) {
    const next = value.cohort_segments.map((s, i) => (i === idx ? { ...s, ...patch } : s));
    onChange({ ...value, cohort_segments: next });
  }

  function addSegment() {
    if (value.cohort_segments.length >= 6) return;
    onChange({
      ...value,
      cohort_segments: [...value.cohort_segments, emptySegment()],
    });
  }

  function removeSegment(idx: number) {
    onChange({
      ...value,
      cohort_segments: value.cohort_segments.filter((_, i) => i !== idx),
    });
  }

  function toggleProcess(segIdx: number, p: WeldProcessType) {
    const seg = value.cohort_segments[segIdx];
    const has = seg.current_process_capability.indexOf(p) !== -1;
    const nextProcs = has
      ? seg.current_process_capability.filter((x) => x !== p)
      : [...seg.current_process_capability, p];
    updateSegment(segIdx, { current_process_capability: nextProcs });
  }

  function updateBlocker(idx: number, patch: Partial<NotableBlocker>) {
    const next = value.notable_blockers.map((b, i) => (i === idx ? { ...b, ...patch } : b));
    onChange({ ...value, notable_blockers: next });
  }

  function addBlocker() {
    if (value.notable_blockers.length >= 5) return;
    onChange({
      ...value,
      notable_blockers: [
        ...value.notable_blockers,
        { blocker_type: "equipment_downtime", affected_weeks: 0, description_short: "" },
      ],
    });
  }

  function removeBlocker(idx: number) {
    onChange({
      ...value,
      notable_blockers: value.notable_blockers.filter((_, i) => i !== idx),
    });
  }

  const segSum = value.cohort_segments.reduce((acc, s) => acc + s.student_count, 0);
  const segMatches = segSum === rosterSize;

  return (
    <div>
      <h2>Where is your cohort right now?</h2>
      <p className="step-description">
        Tell us where your students are along their progression. We use this to
        build a differentiated plan for the time remaining.
      </p>

      <label className="field">
        <span>Weeks remaining in the term</span>
        <input
          type="number"
          min={1}
          max={52}
          value={value.weeks_remaining_in_term}
          onChange={(e) =>
            onChange({ ...value, weeks_remaining_in_term: Number(e.target.value) })
          }
        />
      </label>

      <div className="field">
        <span>Break your {rosterSize} students into groups (segments)</span>
        <small className="field-hint">
          Examples: "Students passing 3F SMAW", "Students still on 2F", "Students
          who passed 1F but not yet on 2F".
        </small>

        <div className="segment-list">
          {value.cohort_segments.map((seg, idx) => (
            <div key={idx} className="segment-block">
              <div className="segment-block-header">Segment {idx + 1}</div>
              <label className="field">
                <span>Label</span>
                <input
                  type="text"
                  value={seg.segment_label}
                  onChange={(e) => updateSegment(idx, { segment_label: e.target.value })}
                  placeholder='e.g., "Advanced — on 3F"'
                />
              </label>
              <div className="field-row">
                <label className="field">
                  <span>Count (of {rosterSize})</span>
                  <input
                    type="number"
                    min={1}
                    max={rosterSize}
                    value={seg.student_count}
                    onChange={(e) => updateSegment(idx, { student_count: Number(e.target.value) })}
                  />
                </label>
                <label className="field">
                  <span>Position capability</span>
                  <select
                    value={seg.current_position_capability}
                    onChange={(e) =>
                      updateSegment(idx, {
                        current_position_capability: e.target.value as WeldPositionCapability,
                      })
                    }
                  >
                    {POSITION_OPTIONS.map((o) => (
                      <option key={o.id} value={o.id}>{o.label}</option>
                    ))}
                  </select>
                </label>
              </div>
              <div className="field">
                <span>Processes</span>
                <div className="checkbox-row-inline">
                  {PROCESS_OPTIONS.map((p) => (
                    <label key={p} className="checkbox-pill">
                      <input
                        type="checkbox"
                        checked={seg.current_process_capability.indexOf(p) !== -1}
                        onChange={() => toggleProcess(idx, p)}
                      />
                      <span>{p}</span>
                    </label>
                  ))}
                </div>
              </div>
              <button
                type="button"
                className="link-button"
                onClick={() => removeSegment(idx)}
              >
                ✕ Remove segment
              </button>
            </div>
          ))}
        </div>

        <button
          type="button"
          className="link-button"
          onClick={addSegment}
          disabled={value.cohort_segments.length >= 6}
        >
          + Add segment {value.cohort_segments.length >= 6 ? "(max 6)" : ""}
        </button>

        <div className={"sum-line " + (segMatches ? "sum-line-ok" : "sum-line-warn")}>
          Segments must sum to {rosterSize}. Currently {segSum} {segMatches ? "✓" : ""}
        </div>
      </div>

      <div className="field">
        <span>Notable blockers in the term so far (optional, up to 5)</span>
        {value.notable_blockers.map((b, idx) => (
          <div key={idx} className="segment-block">
            <div className="field-row">
              <label className="field">
                <span>Type</span>
                <select
                  value={b.blocker_type}
                  onChange={(e) => updateBlocker(idx, { blocker_type: e.target.value as BlockerType })}
                >
                  {BLOCKER_OPTIONS.map((o) => (
                    <option key={o.id} value={o.id}>{o.label}</option>
                  ))}
                </select>
              </label>
              <label className="field">
                <span>Weeks lost</span>
                <input
                  type="number"
                  min={0}
                  max={52}
                  value={b.affected_weeks}
                  onChange={(e) => updateBlocker(idx, { affected_weeks: Number(e.target.value) })}
                />
              </label>
            </div>
            <label className="field">
              <span>Short description (no student names)</span>
              <input
                type="text"
                maxLength={140}
                value={b.description_short}
                onChange={(e) => updateBlocker(idx, { description_short: e.target.value })}
              />
            </label>
            <button
              type="button"
              className="link-button"
              onClick={() => removeBlocker(idx)}
            >
              ✕ Remove
            </button>
          </div>
        ))}
        <button
          type="button"
          className="link-button"
          onClick={addBlocker}
          disabled={value.notable_blockers.length >= 5}
        >
          + Add blocker {value.notable_blockers.length >= 5 ? "(max 5)" : ""}
        </button>
      </div>
    </div>
  );
}
